import {
	Heading,
	Text,
	Box,
	Button,
	Table,
	AsyncSelect,
	Time,
	Inline,
	Icons,
	Tabs,
	TabsList,
	TabContent,
	Stack,
	joinAttributes,
	RelativeTime,
	Dropdown,
	Component,
	Divider,
	Badge,
	Spinner,
	useBreakpoints,
	Select,
} from "@sembark-travel/ui/base"
import { Link, useLocationQuery } from "@sembark-travel/ui/router"
import {
	ListView,
	areAdvancedFiltersAppliedDefault,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui/list"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Omit, Optional } from "utility-types"
import React, { Fragment, useEffect, useId, useMemo } from "react"
import { SelectUsers } from "../Users"
import { ITripPlanRequest, LeadStatusType, TripPlanRequestXHR } from "./store"
import useSWR from "swr"
import { PhoneNumber, Email } from "../Contacts"
import { useCheckPermissions, PERMISSIONS, IUser } from "../Auth"
import {
	Form,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	SelectField,
	parsePhoneNumber,
	DatePickerField,
	SwitchInputField,
	useFieldValue,
	TextAreaInputField,
} from "@sembark-travel/ui/form"
import arrayMutators from "final-form-arrays"
import * as Validator from "yup"
import { AddNewTrip, ITrip } from "../Trips"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDate,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { generatePath, useNavigate } from "../router-utils"
import { ITag, SelectTags } from "../Tags"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"
import { CommentableCommentsList } from "../Comments"

type TFilters = TSearchParams & {
	source?: string
	tags?: Array<ITag>
	owners?: Array<IUser>
	created_after?: Date
	created_before?: Date
	unassigned_only?: boolean
	destinations?: Array<{ id: string; name: string }>
	leadStatus?: Array<{ name: string; id: string }>
	trip_sources?: Array<{ name: string; id: string }>
}

type TFiltersInQuery = TSearchParams & {
	source: "all" | "archived_only" | "new_only" | string
	tags?: Array<string>
	owners?: Array<string>
	ca?: string
	cb?: string
	unassigned_only?: 1
	destinations?: Array<string>
	leadStatus?: Array<string>
	trip_sources?: Array<string>
}

interface IEditTagsAndFollowUpsFormValues {
	tags?: Array<ITag>
	due_at?: Date
	note?: string
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		source,
		tags,
		q,
		page,
		created_after,
		created_before,
		unassigned_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
	} = filters
	const query: TFiltersInQuery = { source: source || "new_only" }
	if (tags?.length) {
		query.tags = tags.map((t) => `${t.id}-${t.name}`)
	}
	if (owners?.length) {
		query.owners = owners.map((t) => `${t.id}-${t.name}`)
	}
	if (destinations?.length) {
		query.destinations = destinations.map((t) => `${t.id}-${t.name}`)
	}
	if (leadStatus?.length) {
		query.leadStatus = leadStatus.map((t) => `${t.id}-${t.name}`)
	}
	if (trip_sources?.length) {
		query.trip_sources = trip_sources.map((v) => v.id)
	}
	if (q?.length) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (unassigned_only) query.unassigned_only = 1
	return query
}

function queryToFilter(query: TFiltersInQuery): TFilters {
	const {
		source,
		tags,
		q,
		page,
		ca,
		cb,
		unassigned_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
	} = query
	const filters: TFilters = { source: source || "new_only" }
	if (tags?.length) {
		filters.tags = tags.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as ITag
		})
	}
	if (owners?.length) {
		filters.owners = owners.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as IUser
		})
	}
	if (destinations?.length) {
		filters.destinations = destinations.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (leadStatus?.length) {
		filters.leadStatus = leadStatus.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { name: string; id: string }
		})
	}
	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((v) => {
			return { name: v, id: v }
		})
	}

	if (q?.length) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (unassigned_only) filters.unassigned_only = true
	return filters
}

export function TripPlanRequestsList() {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilter,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		source: query.source || "new_only",
	})
	const canAssignTeam = hasPermission(PERMISSIONS.MANAGE_TRIP_PLAN_REQUESTS)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { data: tripPlanRequestSources } = useSWR(
		`trip-plan-request-sources`,
		() => TripPlanRequestXHR(xhr).getTripPlanRequestSources()
	)
	const { xs } = useBreakpoints()

	const requestPayload = useMemo(() => {
		const {
			source,
			tags,
			owners,
			leadStatus,
			created_after,
			created_before,
			destinations,
			trip_sources,
			...other
		} = params || {}

		return {
			...other,
			trip_sources:
				source === "archived_only" || source === "new_only"
					? ["all"]
					: source
						? source === "all" && trip_sources?.length
							? trip_sources.map((v) => v.id)
							: [source]
						: null,
			archived_only: source === "archived_only" ? 1 : null,
			new_only: source === "new_only" ? 1 : null,
			tags: tags?.map((t) => t.id),
			owners: owners?.map((t) => t.id),
			destinations: destinations?.map((t) => t.id),
			include: leadStatus?.map((t) => t.id).join(","),

			created_after: created_after
				? dateToUTCString(startOf(created_after, "day"))
				: null,
			created_before: created_before
				? dateToUTCString(endOf(created_before, "day"))
				: null,
		}
	}, [params])
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Trip Plan Requests"
				resetParams={({ source }) => ({
					source,
					page: 1,
				})}
				areAdvancedFiltersApplied={(params) => {
					const { source, unassigned_only, ...otherParams } = params
					return (
						areAdvancedFiltersAppliedDefault(otherParams) ||
						Boolean(unassigned_only)
					)
				}}
				actions={
					canAssignTeam ? (
						<Inline gap={"2"}>
							<Link
								to={generatePath(
									"/trip-plan-requests/auto-distribution-preferences"
								)}
								color="accent"
							>
								<Badge status="info">
									<Box paddingX={"2"}>
										<Icons.Cog /> <Icons.NewFeature width="10" height="6" />
									</Box>
								</Badge>
							</Link>
							<Link
								to={generatePath("/trip-plan-requests/report")}
								color="accent"
							>
								<Button level="primary" status="primary">
									Report
									<Icons.NewFeature width="10" height="6" />
								</Button>
							</Link>

							<Dropdown alignRight="sm">
								<Dropdown.ToggleButton>
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<TemporaryStorage
										initUrl="/trip-plan-requests"
										initParams={{
											...requestPayload,
											_download: 1,
											timezone_offset: config.timezoneOffset,
										}}
									>
										{({ message, generate, url, reset }) =>
											url ? (
												<Dropdown.MenuItem
													as="a"
													href={url}
													target="_blank"
													download
													onClick={() => reset()}
												>
													{message}
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														generate()
													}}
												>
													{message || (
														<>
															<Icons.DocumentDownload /> Download as CSV/Excel
														</>
													)}
												</Dropdown.MenuItem>
											)
										}
									</TemporaryStorage>
								</Dropdown.Menu>
							</Dropdown>
						</Inline>
					) : null
				}
				Filters={Filters}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs>
						<TabsList>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="new_only"
								key="new_only"
							>
								New
							</TabLink>
							{tripPlanRequestSources?.map((a) => (
								<TabLink
									key={a.name}
									params={searchParams}
									setParams={setSearchParams}
									source={a.name}
								>
									<Box
										style={{ maxWidth: "100px" }}
										textOverflow="truncate"
										title={a.name}
									>
										{a.name}
									</Box>
								</TabLink>
							))}
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="all"
								key="all_all"
							>
								All
							</TabLink>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="archived_only"
								key="archived_only"
							>
								Archived
							</TabLink>
						</TabsList>
						<TabContent style={{ minHeight: "50vh" }}>
							<ListView<ITripPlanRequest, TFilters>
								params={params}
								pageKey="trip-plan-requests"
								swrConfig={{ revalidateOnFocus: true }}
								fetch={(xhr, params) => {
									return TripPlanRequestXHR(xhr).getTripPlanRequests(
										requestPayload
									)
								}}
								onPageChange={(page) => setParams({ ...params, page })}
							>
								{({ items, refresh }) =>
									xs ? (
										<Box>
											<Stack gap="2" as="ol">
												{items.map((r) => (
													<Box
														as="li"
														key={r.id}
														bgColor="default"
														padding="4"
														rounded="md"
														borderWidth="1"
													>
														<Box style={{ float: "right" }} paddingLeft="2">
															<Actions
																request={r}
																onChange={refresh}
																canAssignTeam={canAssignTeam}
															/>
														</Box>
														<Stack gap="2">
															<GuestDetails
																request={r}
																showFullDetailsInDialog
																showSource={
																	(params.source === "all" ||
																		params.source === "new_only") &&
																	Number(tripPlanRequestSources?.length || 0) >
																		1
																}
															/>
															{canAssignTeam ? (
																<Inline gap="1">
																	<Box>
																		<Icons.User
																			title="Team Member"
																			color="muted"
																		/>
																	</Box>
																	<Box flex="1" minWidth="0">
																		{r.owner ? (
																			<Stack>
																				{r.owner.name}
																				{r.assigned_at ? (
																					<Box>
																						<RelativeTime
																							fontSize="sm"
																							timestamp={r.assigned_at}
																							color="muted"
																						/>
																					</Box>
																				) : null}
																			</Stack>
																		) : (
																			<Text color="muted">Unassigned</Text>
																		)}
																	</Box>
																</Inline>
															) : null}
															<Stack gap="2">
																{r.tags?.length || r.notes?.length ? (
																	<>
																		<Inline gap="1">
																			<Inline gap="1" flexWrap="wrap">
																				{(r.tags || []).map((t) => (
																					<Badge key={t.id}>{t.name}</Badge>
																				))}
																			</Inline>
																		</Inline>

																		{/* Follow up notes and comments are same so using same component */}
																		<CommentableCommentsList
																			comments={r.notes || []}
																			onChange={refresh}
																		/>
																	</>
																) : (
																	<Text color="muted">-</Text>
																)}
																{r.deleted_at ? (
																	<Text color="warning">
																		<Icons.Trash title="Archived" />{" "}
																		{r.deleted_by?.name || "N/A"},{" "}
																		<RelativeTime timestamp={r.deleted_at} />
																	</Text>
																) : null}
															</Stack>
														</Stack>
													</Box>
												))}
											</Stack>
										</Box>
									) : (
										<Table
											headers={[
												"Contact",
												"Travel Details",
												"Tags and Follow-Ups",
											].concat(canAssignTeam ? ["Team", ""] : [""])}
											alignCols={{ 3: "right", 4: "right" }}
											hover
											bordered
											responsive
											rows={items.map((r) =>
												[
													<GuestDetails
														request={r}
														showFullDetailsInDialog
														showSource={
															(params.source === "all" ||
																params.source === "new_only") &&
															Number(tripPlanRequestSources?.length || 0) > 1
														}
													/>,
													<Stack gap="px" maxWidth="md" wordBreak="words">
														{r.destination ? (
															<Text fontWeight="semibold">{r.destination}</Text>
														) : null}
														<Inline gap="px">
															{joinAttributes(
																r.start_date ? (
																	<Time
																		value={parseDate(
																			r.start_date,
																			"YYYY-MM-DD"
																		)}
																	/>
																) : null,
																r.no_of_days
																	? `${r.no_of_days - 1}N, ${r.no_of_days}D`
																	: null
															)}
														</Inline>
														{r.hotel_preference ? (
															<Text maxWidth="xs" fontSize="sm">
																{r.hotel_preference}
															</Text>
														) : null}

														{r.extra_fields ? (
															<Stack>
																{Object.keys(r.extra_fields).map((key) => (
																	<Text key={key}>
																		{key}:{" "}
																		{
																			JSON.stringify(
																				(r.extra_fields?.[key] as never) || ""
																			) as string
																		}
																	</Text>
																))}
															</Stack>
														) : null}
														{r.comments ? (
															<Text>comment: {r.comments}</Text>
														) : null}
													</Stack>,
													<Stack gap="2">
														{r.tags?.length || r.notes?.length ? (
															<>
																<Inline gap="1">
																	<Inline gap="1" flexWrap="wrap">
																		{(r.tags || []).map((t) => (
																			<Badge key={t.id}>{t.name}</Badge>
																		))}
																	</Inline>
																</Inline>
																<CommentableCommentsList
																	comments={r.notes || []}
																	onChange={refresh}
																/>
															</>
														) : (
															<Text color="muted">-</Text>
														)}

														{/* Follow up notes and comments are same so using same component */}
														{r.deleted_at ? (
															<Text color="warning">
																<Icons.Trash title="Archived" />{" "}
																{r.deleted_by?.name || "N/A"},{" "}
																<RelativeTime timestamp={r.deleted_at} />
															</Text>
														) : null}
													</Stack>,
												]
													.concat(
														canAssignTeam
															? [
																	<Box>
																		<Box>
																			{r.owner ? (
																				<Stack gap="1">
																					{r.owner.name}
																					{r.assigned_at ? (
																						<Box>
																							<RelativeTime
																								timestamp={r.assigned_at}
																							/>
																						</Box>
																					) : null}
																				</Stack>
																			) : (
																				<Box>-</Box>
																			)}
																		</Box>
																	</Box>,
																]
															: []
													)
													.concat([
														<Actions
															request={r}
															onChange={refresh}
															canAssignTeam={canAssignTeam}
														/>,
													])
											)}
										/>
									)
								}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Fragment>
	)
}

function GuestDetails({
	request: r,
	showSource,
	showFullDetailsInDialog,
}: {
	request: ITripPlanRequest
	showSource?: boolean
	showFullDetailsInDialog?: boolean
}) {
	return (
		<Stack gap="2">
			<Stack gap="1">
				{showSource ? (
					<Inline alignItems="center" color="muted" gap="1" fontSize="sm">
						<Icons.ChevronDown rotate="270" title="Source" />
						<Text>{r.trip_source}</Text>
					</Inline>
				) : null}
				<Inline gap="1" alignItems="baseline">
					<Component initialState={false}>
						{({ state, setState }) => (
							<>
								<a
									href="#view-details"
									onClick={(e) => {
										e.preventDefault()
										if (showFullDetailsInDialog) {
											setState(true)
										}
									}}
								>
									<Heading
										as="h4"
										fontSize="md"
										color={showFullDetailsInDialog ? "accent" : undefined}
									>
										{r.name}
									</Heading>
								</a>
								<Dialog
									open={state}
									onClose={() => setState(false)}
									title="Trip Plan Request Details"
								>
									<Dialog.Body>
										<TripPlanRequestDetails request={r} />
									</Dialog.Body>
									<Dialog.Footer>
										<Button onClick={() => setState(false)}>Ok</Button>
									</Dialog.Footer>
								</Dialog>
							</>
						)}
					</Component>
					{r.no_of_adults || r.no_of_adults ? (
						<Text fontWeight="semibold">
							(
							{[
								r.no_of_adults ? `${r.no_of_adults}A` : null,
								r.no_of_children ? `${r.no_of_children}C` : null,
							]
								.filter(Boolean)
								.join(" + ")}
							)
						</Text>
					) : null}
				</Inline>
			</Stack>
			<Stack gap="px">
				{r.phone_number ? (
					<Box>
						<PhoneNumber value={r.phone_number} />
					</Box>
				) : null}
				{r.email ? (
					<Box>
						<Email value={r.email} />
					</Box>
				) : null}
				{r.created_at && (
					<Text fontSize="base" color={"muted"} style={{ fontStyle: "italic" }}>
						- <RelativeTime timestamp={r.created_at} />
					</Text>
				)}
			</Stack>
		</Stack>
	)
}

function Actions({
	request: r,
	onChange: refresh,
	canAssignTeam,
}: {
	request: ITripPlanRequest
	onChange: () => void
	canAssignTeam: boolean
}) {
	const xhr = useXHR()
	const navigate = useNavigate()

	return (
		<Inline justifyContent="end" alignItems="center" gap="2">
			{r.trip_id ? (
				<Link
					to={`/trips/${r.trip_id}`}
					color="accent"
					fontWeight="semibold"
					anchored
				>
					#&nbsp;{r.trip_id}
				</Link>
			) : !r.deleted_at ? (
				<CreateNewQueryFromRequestInDialog
					request={r}
					onSuccess={(trip) => {
						navigate("/trips/:tripId", {
							params: { tripId: trip.id.toString() },
						})
					}}
				>
					{({ create }) => (
						<Button title="Create New Query" size="sm" onClick={create}>
							<Icons.Plus />
						</Button>
					)}
				</CreateNewQueryFromRequestInDialog>
			) : null}
			<Box>
				<Component initialState={false}>
					{({ state: isEditing, setState: changeIsEditing }) => (
						<>
							<Dialog
								open={isEditing}
								onClose={() => changeIsEditing(false)}
								title="Assign Team Member"
								sm
							>
								<Dialog.Body>
									<Form<{ owner: typeof r.owner }>
										initialValues={{
											owner: r.owner,
										}}
										validate={validateFormValues(
											Validator.object().shape({
												owner: Validator.object()
													.nullable(true)
													.typeError("Please select a user to assign."),
											})
										)}
										onSubmit={withServerErrors(async (values) => {
											const { owner } = values
											await TripPlanRequestXHR(xhr).assignTripRequestsOwner(
												[r.id],
												owner?.id
											)
											changeIsEditing(false)
											refresh()
											if (!owner) {
												showSnackbar("Team member remove from the request.")
											} else {
												showSnackbar("Team member assigned to the request.")
											}
										})}
										subscription={{
											submitting: true,
										}}
									>
										{({ submitting, handleSubmit }) => (
											<form noValidate onSubmit={handleSubmit}>
												<SelectField
													select={SelectUsers}
													name="owner"
													fetchOnMount
													multiple={false}
													label="Select Team Member"
												/>
												<Divider sm />
												<Stack gap="4">
													<SubmissionError />
													<Inline gap="4">
														<Button type="submit" disabled={submitting}>
															{submitting ? "Updating..." : "Assign"}
														</Button>
														<Button
															type="button"
															disabled={submitting}
															onClick={() => changeIsEditing(false)}
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</form>
										)}
									</Form>
								</Dialog.Body>
							</Dialog>

							<EditTagsAndFollowUpsDialog request={r} onChange={refresh}>
								{({ onEdit: editTags }) => (
									<Dropdown alignRight>
										<Dropdown.ToggleButton level="tertiary" size="sm">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<Dropdown.MenuItem onClick={() => editTags()}>
												<Icons.Tags /> Edit Tags and Follow-Ups
											</Dropdown.MenuItem>
											{!r.trip_id ? (
												<>
													{canAssignTeam ? (
														<Dropdown.MenuItem
															onClick={() => changeIsEditing(true)}
														>
															<Icons.User /> Assign Team
														</Dropdown.MenuItem>
													) : null}
													<Dropdown.MenuItemDivider />
													{!r.deleted_at ? (
														<Dropdown.MenuItem
															onClick={async () => {
																if (
																	window.confirm(
																		"Are you sure you want to archive this plan request ? You can restore it any time."
																	)
																) {
																	const { message } = await TripPlanRequestXHR(
																		xhr
																	).archiveTripPlanRequest(r.id)
																	refresh()
																	showSnackbar(
																		message || "Request archived successfully"
																	)
																}
															}}
														>
															<Icons.Trash color="danger" /> Archive Request
														</Dropdown.MenuItem>
													) : (
														<Dropdown.MenuItem
															onClick={async () => {
																if (
																	window.confirm(
																		"Are you sure you want to restore this archived trip plan request ?"
																	)
																) {
																	const { message } = await TripPlanRequestXHR(
																		xhr
																	).restoreTripPlanRequest(r.id)
																	refresh()
																	showSnackbar(
																		message || "Request restored successfully"
																	)
																}
															}}
														>
															<Icons.Refresh color="success" /> Restored Request
														</Dropdown.MenuItem>
													)}
												</>
											) : null}
										</Dropdown.Menu>
									</Dropdown>
								)}
							</EditTagsAndFollowUpsDialog>
						</>
					)}
				</Component>
			</Box>
		</Inline>
	)
}

function Filters() {
	const { value: type } = useFieldValue<string | undefined>("source")
	const getLeadStatusType = useMemo(
		() =>
			type === "new_only"
				? LeadStatusType.filter(
						(status) =>
							status.id === "active_followups" ||
							status.id === "pending_followups"
					)
				: LeadStatusType,
		[type]
	)

	return (
		<Stack gap="4">
			<SelectField
				label="Tags"
				name="tags"
				select={SelectTags}
				type="trip-plan-request"
				fetchOnMount
				multiple
			/>
			<Stack gap="1">
				<label htmlFor="field_created_after">Created During</label>
				<Box display="flex" alignItems="center">
					<DatePickerField name="created_after" dateFormat="DD MMM, YYYY" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField
						name="created_before"
						rightAlign
						dateFormat="DD MMM, YYYY"
					/>
				</Box>
			</Stack>
			<SelectField
				label="Team Members"
				name="owners"
				select={SelectUsers}
				multiple
			/>
			<SelectField
				label="Destinations"
				name="destinations"
				select={SelectTripPlanRequestDestinations}
				multiple
			/>
			{type === "archived_only" ? null : (
				<SelectField
					select={Select}
					name="leadStatus"
					label="Status Type"
					options={getLeadStatusType}
					multiple
				/>
			)}
			{type !== "all" ? null : (
				<SelectField
					select={SelectLeadSources}
					name="trip_sources"
					label="Trip Sources"
					multiple
				/>
			)}

			<SwitchInputField label="Unassigned Team" name="unassigned_only" />
		</Stack>
	)
}

export function SelectTripPlanRequestDestinations(
	props: Optional<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) =>
				TripPlanRequestXHR(xhr)
					.getTripPlanRequestDestinations({ q })
					.then((resp) => resp)
			}
			{...props}
		/>
	)
}

function TripPlanRequestDetails({ request }: { request: ITripPlanRequest }) {
	return (
		<Inline gap="6" flexWrap="wrap">
			<Stack gap="1">
				<Heading fontSize="base" color="primary">
					Guest Details
				</Heading>
				<GuestDetails request={request} showSource={false} />
			</Stack>
			<Stack gap="1">
				<Heading fontSize="base" color="primary">
					Destination
				</Heading>
				<Stack gap="px">
					{request.destination ? (
						<Text fontWeight="semibold" fontSize="md">
							{request.destination}
						</Text>
					) : null}
					<Inline gap="px">
						{joinAttributes(
							request.start_date ? (
								<Time value={parseDate(request.start_date, "YYYY-MM-DD")} />
							) : null,
							request.no_of_days
								? `${request.no_of_days - 1}N,${request.no_of_days}D`
								: null
						)}
					</Inline>
				</Stack>
			</Stack>
			<Stack gap="1" maxWidth="sm" wordBreak="words">
				<Heading fontSize="base" color="primary">
					More Details
				</Heading>
				<Stack gap="px">
					{request.comments ? (
						<Box maxWidth="xs" whiteSpace="preserveLine">
							{request.comments}
						</Box>
					) : null}
					{request.hotel_preference ? (
						<Text>{request.hotel_preference}</Text>
					) : null}
					{request.extra_fields ? (
						<Stack>
							{Object.keys(request.extra_fields).map((key) => (
								<Text key={key}>
									{key}:{" "}
									{
										JSON.stringify(
											(request.extra_fields?.[key] as never) || ""
										) as string
									}
								</Text>
							))}
						</Stack>
					) : null}
					<Box>
						<Badge success>{request.trip_source}</Badge>
					</Box>
				</Stack>
			</Stack>
		</Inline>
	)
}

function CreateNewQueryFromRequestInDialog({
	children,
	request,
	onSuccess,
}: {
	request: ITripPlanRequest
	children: (props: { create: () => void }) => React.ReactNode
	onSuccess: (trip: ITrip) => void
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({ create: openDialog })}
			<Dialog
				open={isOpenDialog}
				onClose={closeDialog}
				title="Create New Query from Request"
				lg
			>
				<Dialog.Body>
					<TripPlanRequestDetails request={request} />
					<Divider />
					<CreateNewQueryFromRequest
						request={request}
						onSuccess={(trip) => {
							closeDialog()
							onSuccess(trip)
						}}
						onCancel={closeDialog}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function CreateNewQueryFromRequest({
	request,
	onSuccess,
	onCancel,
}: {
	request: ITripPlanRequest
	onSuccess: (trip: ITrip) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const requestId = useId()
	const { data } = useSWR(
		`trip-plan-request-to-trip/${request.id}?${requestId}`,
		() =>
			TripPlanRequestXHR(xhr)
				.getPayloadForTrip(request.id)
				.then((payload) => {
					const parsedPhoneNumbers = payload.tourist.phone_numbers?.length
						? payload.tourist.phone_numbers.map((number) =>
								parsePhoneNumber(number, "IN")
							)
						: []
					return {
						reference_id: payload.reference_id || "",
						start_date: payload.start_date
							? startOf(parseDate(payload.start_date, "YYYY-MM-DD"), "day")
							: undefined,
						no_of_days: payload.no_of_days,
						no_of_nights: payload.no_of_nights,
						destinations: payload.destinations || [],
						no_of_adults: payload.no_of_adults,
						children: payload.children || [],
						trip_source: payload.trip_source,
						comments: payload.comments || "",
						tourist: {
							name: request.name,
							email: request.email || "",
							phone_numbers: parsedPhoneNumbers
								.map((parsedPhoneNumber) =>
									parsedPhoneNumber?.nationalNumber
										? {
												is_primary: true,
												country_code: parsedPhoneNumber?.country,
												number: parsedPhoneNumber?.nationalNumber,
												phone_number: parsedPhoneNumber?.number,
											}
										: undefined
								)
								.filter((p): p is NonNullable<typeof p> => Boolean(p)),
						},
						trip_source_contact: undefined,
					}
				})
	)

	if (!data) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<AddNewTrip
			initialValues={data}
			onSuccess={async (trip) => {
				return TripPlanRequestXHR(xhr)
					.attachTrip(request.id, { trip_id: trip.id })
					.then(() => {
						onSuccess(trip)
						showSnackbar(`Query (ID: ${trip.id}) Initiated for this request.`)
					})
					.catch((e) => {
						alert(e.message || "Something went wrong")
					})
			}}
			onCancel={onCancel}
		/>
	)
}

export function SelectTripPlanRequests(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			fetch={(q) =>
				TripPlanRequestXHR(xhr)
					.getTripPlanRequests({ q })
					.then((resp) => resp.data)
			}
			{...props}
		/>
	)
}

function TabLink({
	params,
	setParams,
	children,
	source,
}: {
	params: TFilters
	setParams: (params: TFilters) => void
	children: React.ReactNode
	showCount?: boolean
	extraParams?: unknown
	source?: string
}) {
	return (
		<li className={(source ? params.source === source : true) ? "active" : ""}>
			<a
				href={`#${source}`}
				onClick={(e) => {
					e.preventDefault()
					const newParams = { ...params }
					if (source) {
						newParams.source = source
					}
					setParams(newParams)
				}}
			>
				{children}
			</a>
		</li>
	)
}

export function SelectLeadSources({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const { data } = useSWR(`/trip-plan-request-sources`, () =>
		TripPlanRequestXHR(xhr).getTripPlanRequestSources()
	)
	return <Select {...props} options={data || []} />
}

const EditTagsAndFollowUpsValidation = validateFormValues(
	Validator.object().shape({
		tags: Validator.array().of(
			Validator.object().shape({
				name: Validator.string().required(),
			})
		),
		due_at: Validator.date().nullable(),
		note: Validator.string()
			.trim()
			.min(3, "Follow up Note must be at least 3 characters")
			.max(500, "Follow up Note cannot be more than 500 characters")
			.when("due_at", {
				is: (due_at: Date) => due_at,
				then: (schema) =>
					schema.required("Follow up note is required when due date is set"),
				otherwise: (schema) => schema.nullable(),
			}),
	})
)

function EditTagsAndFollowUpsDialog({
	children,
	request,
	onChange,
}: {
	children: (props: { onEdit: () => void }) => React.ReactNode
	request: ITripPlanRequest
	onChange: () => void
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onEdit: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Tags and Follow-Ups" sm>
				<Dialog.Body>
					<EditTagsAndFollowUpsForm
						request={request}
						onCancel={close}
						onChange={() => {
							close()
							onChange()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditTagsAndFollowUpsForm({
	request,
	onCancel,
	onChange,
}: {
	request: ITripPlanRequest
	onChange: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<Form<IEditTagsAndFollowUpsFormValues>
			initialValues={{
				tags: request.tags?.map((t) => ({ id: t.id, name: t.name })) || [],
			}}
			validate={EditTagsAndFollowUpsValidation}
			onSubmit={withServerErrors(async (values) => {
				await TripPlanRequestXHR(xhr).editTagsAndFollowUps(request.id, {
					...values,
					tags: values.tags?.map((t) => t.name),
					due_at: values.due_at ? dateToUTCString(values.due_at) : undefined,
				})
				showSnackbar("Tags and Follow-Ups updated successfully")
				onChange()
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<SelectField
							select={SelectTags}
							name="tags"
							fetchOnMount
							label="Select existing or create new tags"
							type={"trip-plan-request"}
							multiple
						/>

						<Inline gap="2" width="full" flexWrap={"wrap"}>
							<Box flex="1" minWidth="max">
								<TextAreaInputField
									label="Follow Up Note"
									name="note"
									placeholder="Enter follow up note"
									rows={3}
									help="Maximum of 500 characters"
									width="full"
								/>
							</Box>

							<DatePickerField
								label="Due Date"
								name="due_at"
								dateFormat="MMMM D, YYYY"
								help="If it needs to be resolved at a specific time"
								timeFormat
							/>
						</Inline>

						<Stack gap="4">
							<SubmissionError />
							<Inline gap="4">
								<Button disabled={submitting} type="submit">
									{submitting ? "Saving..." : "Save"}
								</Button>
								{onCancel ? (
									<Button onClick={onCancel} disabled={submitting}>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}
